"use client";

import Link from "next/link";
import { useState } from "react";
import { BookOpen, Zap, Trophy, Plus, Upload, Link as LinkIcon, FileSpreadsheet, ChevronRight, Brain } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { WORD_LISTS } from "@/lib/vocab/sampleData";

const difficultyColors: Record<string, string> = {
  green: "bg-green-100 text-green-700",
  blue: "bg-blue-100 text-blue-700",
  purple: "bg-purple-100 text-purple-700",
};

export default function VocabHubPage() {
  const [showImport, setShowImport] = useState(false);

  const poolWords = 12;
  const studyStreak = 5;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-primary)" }}>
      {/* Nav */}
      <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              ← Back
            </Link>
            <span style={{ color: "var(--border)" }}>|</span>
            <div className="flex items-center gap-2">
              <Brain size={18} className="text-blue-500" />
              <span className="font-semibold" style={{ color: "var(--text-primary)" }}>VocabMaster</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Beta</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Hero row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* My Pool CTA */}
          <div className="md:col-span-2 rounded-2xl p-6 text-white relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)" }}>
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10" />
            <div className="absolute -right-2 bottom-0 w-20 h-20 rounded-full bg-white/5" />
            <p className="text-blue-100 text-sm mb-1">Active Learning Pool</p>
            <div className="flex items-end gap-2 mb-3">
              <span className="text-5xl font-bold">{poolWords}</span>
              <span className="text-blue-200 mb-1">words</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-100 mb-5">
              <Zap size={14} />
              <span>{studyStreak} day streak</span>
              <span className="mx-2 opacity-40">·</span>
              <span>3 mastered this week</span>
            </div>
            <div className="flex gap-3">
              <Link href="/vocab/study"
                className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50 transition-colors">
                Study Flashcards
              </Link>
              <Link href="/vocab/quiz"
                className="px-4 py-2 bg-white/20 text-white rounded-lg font-medium text-sm hover:bg-white/30 transition-colors">
                Take Quiz
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="rounded-2xl p-5 border" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
            <p className="text-sm font-medium mb-4" style={{ color: "var(--text-muted)" }}>Your Progress</p>
            <div className="space-y-3">
              {[
                { label: "Total words studied", value: "68", icon: BookOpen },
                { label: "Mastered", value: "8", icon: Trophy },
                { label: "In pool now", value: "12", icon: Brain },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon size={14} style={{ color: "var(--text-muted)" }} />
                    <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{label}</span>
                  </div>
                  <span className="font-bold text-blue-600">{value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
              <div className="flex justify-between text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                <span>TOEFL 500 progress</span>
                <span>68/500</span>
              </div>
              <div className="h-2 rounded-full" style={{ backgroundColor: "var(--bg-secondary)" }}>
                <div className="h-2 rounded-full bg-blue-500" style={{ width: "13.6%" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Add to pool */}
        <div className="rounded-2xl border mb-8 overflow-hidden" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
          <button
            onClick={() => setShowImport(v => !v)}
            className="w-full flex items-center justify-between px-6 py-4 hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center gap-2">
              <Plus size={16} className="text-blue-500" />
              <span className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>Add words to your pool</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-muted)" }}>
                URL · PDF · Excel · Manual
              </span>
            </div>
            <ChevronRight size={16} style={{ color: "var(--text-muted)", transform: showImport ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.2s" }} />
          </button>
          {showImport && (
            <div className="px-6 pb-5 border-t grid grid-cols-1 sm:grid-cols-3 gap-3" style={{ borderColor: "var(--border)" }}>
              <div className="mt-4 sm:col-span-3">
                <label className="text-xs font-medium mb-1 block" style={{ color: "var(--text-muted)" }}>Paste a URL (article, blog post, Wikipedia page…)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="https://en.wikipedia.org/wiki/Quantum_mechanics"
                    className="flex-1 px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: "var(--bg-secondary)", borderColor: "var(--border)", color: "var(--text-primary)" }}
                  />
                  <button className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                    <LinkIcon size={14} /> Extract Words
                  </button>
                </div>
              </div>
              <button className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed text-sm font-medium transition-colors hover:bg-blue-50"
                style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                <Upload size={18} className="text-blue-400" /> Upload PDF
              </button>
              <button className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed text-sm font-medium transition-colors hover:bg-green-50"
                style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                <FileSpreadsheet size={18} className="text-green-500" /> Upload Excel / CSV
              </button>
              <button className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed text-sm font-medium transition-colors hover:bg-purple-50"
                style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                <Plus size={18} className="text-purple-400" /> Add Words Manually
              </button>
            </div>
          )}
        </div>

        {/* Word Lists */}
        <div>
          <h2 className="text-lg font-bold mb-4" style={{ color: "var(--text-primary)" }}>Word List Library</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WORD_LISTS.map(list => (
              <div key={list.id} className="rounded-2xl border p-5 flex flex-col hover:shadow-md transition-shadow"
                style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{list.name}</h3>
                    <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{list.description}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${difficultyColors[list.difficultyColor]}`}>
                    {list.difficulty}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                  <span>{list.wordCount} words</span>
                  {list.started && (
                    <>
                      <span>·</span>
                      <span className="text-blue-500">{list.inPool} in pool</span>
                    </>
                  )}
                </div>

                {list.started && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                      <span>Diagnosed</span>
                      <span>{list.progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full" style={{ backgroundColor: "var(--bg-secondary)" }}>
                      <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width: `${list.progress}%` }} />
                    </div>
                  </div>
                )}

                <div className="mt-auto">
                  <Link href="/vocab/diagnostic"
                    className="w-full block text-center py-2 rounded-lg text-sm font-medium transition-colors"
                    style={list.started
                      ? { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }
                      : { backgroundColor: "#2563eb", color: "#fff" }}>
                    {list.started ? "Continue Diagnostic" : "Start Diagnostic"}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pool quick link */}
        <div className="mt-6 text-center">
          <Link href="/vocab/pool" className="inline-flex items-center gap-2 text-sm text-blue-500 hover:text-blue-700 transition-colors">
            View all words in my pool <ChevronRight size={14} />
          </Link>
        </div>

      </div>
    </div>
  );
}
